import React, { useEffect, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EastIcon from "@mui/icons-material/East";
import {
    Box,
    Button,
    Chip,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { useSelector } from "react-redux";

import StandartButton from "components/atoms/StandartButton";
import { GenericModal } from "components/GenericModal";

import * as Styled from "./AddComparisationModal.styled";

const AddComparisationModal = ({
    matchFields,
    setMatchFields,
    isOpen,
    setIsOpen,
    fieldIndex,
}) => {
    const language = useSelector(
        (state: any) => state.languagesState.languages,
    );
    const singleConnection = useSelector(
        (state: any) =>
            state.leadAllConnectionState.getAllLeadConnection.singleConnection,
    );

    const sourceField = useMemo(
        () =>
            matchFields[fieldIndex]?.source_field ||
            matchFields[fieldIndex]?.source,
        [singleConnection],
    );

    const rows =
        matchFields?.[fieldIndex]?.compared_fields?.[sourceField] || [];
    const hasFilledRows = rows.some(
        ({ compare_to, map_to }) => compare_to || map_to,
    );

    const handleSave = () => {
        setIsOpen(false);
        setMatchFields((prev) => {
            prev[fieldIndex].compared_fields[sourceField] = prev[
                fieldIndex
            ].compared_fields?.[sourceField]?.filter(
                ({ compare_to, map_to }) => !!compare_to && !!map_to,
            );
            return prev;
        });
    };

    const handleAddRow = () => {
        setMatchFields((prev) => {
            const newList = JSON.parse(JSON.stringify(prev)); // Deep copy
            const comparedFieldsForSource =
                newList[fieldIndex].compared_fields?.[sourceField] || [];
            newList[fieldIndex].compared_fields[sourceField] = [
                ...comparedFieldsForSource,
                {
                    map_to: "",
                    compare_to: "",
                },
            ];
            return newList;
        });
    };

    const handleDeleteRow = (index) => {
        setMatchFields((prev) => {
            const newList = JSON.parse(JSON.stringify(prev)); // Deep copy

            newList[fieldIndex]?.compared_fields?.[sourceField]?.splice(
                index,
                1,
            );
            return newList;
        });
    };

    const handleFieldChange = (name, index, value) => {
        setMatchFields((prev) => {
            const newList = JSON.parse(JSON.stringify(prev)); // Deep copy
            newList[fieldIndex].compared_fields[sourceField][index][name] =
                value;
            return newList;
        });
    };

    useEffect(() => {
        if (sourceField) {
            setMatchFields((prev) => {
                const newList = JSON.parse(JSON.stringify(prev)); // Deep copy

                if (!newList[fieldIndex].compared_fields) {
                    newList[fieldIndex].compared_fields = {};
                }

                if (
                    !newList[fieldIndex]?.compared_fields?.[sourceField]?.length
                ) {
                    newList[fieldIndex].compared_fields = {
                        [sourceField]: [
                            {
                                compare_to: "",
                                map_to: "",
                            },
                        ],
                    };
                }
                return newList;
            });
        }
    }, [matchFields?.length, sourceField]);

    const content = (
        <Box sx={{ px: 3, py: 2 }}>
            <Typography
                sx={{
                    fontSize: "13px",
                    color: "#4a4a4f",
                    fontFamily: '"Nunito", sans-serif',
                    mb: 1.5,
                }}
            >
                {language?.lead?.comparisons_how_it_works ??
                    "When the incoming value of this field matches a “Compare to” value, it is replaced with the “Map to” value before being sent to the destination."}
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    p: 1.5,
                    mb: 2.5,
                    borderRadius: "10px",
                    border: "1px dashed #d5d5da",
                    backgroundColor: "#fafafa",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#86868b",
                        fontFamily: '"Nunito", sans-serif',
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                    }}
                >
                    {language?.lead?.example ?? "Example"}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "13px",
                        color: "#1c1c1e",
                        fontFamily: '"Nunito", sans-serif',
                    }}
                >
                    {language?.lead?.comparisons_example_prefix ??
                        "City comes as"}
                </Typography>
                <Chip
                    size="small"
                    label="New York"
                    variant="outlined"
                    onClick={() => {}}
                />
                <EastIcon sx={{ fontSize: 16, color: "#86868b" }} />
                <Chip
                    size="small"
                    label="NWY"
                    sx={{
                        backgroundColor: "#1c1c1e",
                        color: "#ffffff",
                        fontWeight: 700,
                    }}
                    onClick={() => {}}
                />
                <Typography
                    sx={{
                        fontSize: "13px",
                        color: "#1c1c1e",
                        fontFamily: '"Nunito", sans-serif',
                    }}
                >
                    {language?.lead?.comparisons_example_suffix ??
                        "is sent to the destination."}
                </Typography>
            </Box>
            <Styled.Rows>
                {rows.map((field, index) => {
                    return (
                        <Styled.Row key={index}>
                            <TextField
                                fullWidth
                                size="small"
                                label={
                                    language?.lead?.compare_to ?? "Compare to"
                                }
                                placeholder="New York"
                                variant="outlined"
                                value={field?.compare_to}
                                onChange={(e) =>
                                    handleFieldChange(
                                        "compare_to",
                                        index,
                                        e.target.value,
                                    )
                                }
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label={language?.lead?.map_to ?? "Map to"}
                                placeholder="NWY"
                                variant="outlined"
                                value={field?.map_to}
                                onChange={(e) =>
                                    handleFieldChange(
                                        "map_to",
                                        index,
                                        e.target.value,
                                    )
                                }
                            />
                            <IconButton
                                color="error"
                                onClick={() => handleDeleteRow(index)}
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Styled.Row>
                    );
                })}
            </Styled.Rows>
            <Box sx={{ mt: 2 }}>
                <StandartButton
                    variant={ButtonVariant.BLACK_OUTLINED}
                    onClick={handleAddRow}
                >
                    <AddIcon />
                    {language?.lead?.add_row ?? "Add Row"}
                </StandartButton>
            </Box>
        </Box>
    );

    const footer = (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button color="primary" variant="contained" onClick={handleSave}>
                {language?.buttons?.save ?? "Save"}
            </Button>
        </Box>
    );

    return (
        <GenericModal
            open={isOpen}
            onClose={handleSave}
            title={
                hasFilledRows
                    ? (language?.lead?.edit_comparison_fields ??
                      "Edit Comparison Fields")
                    : (language?.lead?.add_comparison_fields ??
                      "Add Comparison Fields")
            }
            subtitle={sourceField}
            maxWidth="sm"
            content={content}
            footer={footer}
        />
    );
};

export default AddComparisationModal;
